import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BulkEditStatusPopover } from 'src/components/common/BulkActions/BulkEditStatusPopover';
import { MultiSelectionToggleGlobal } from 'src/components/common/MultiSelect/Toggle/MultiSelectionToggleGlobal';
import { FilterToolbar } from 'src/components/FilterToolbar';
import { QuickFilterToolbarItem } from 'src/components/FilterToolbar/QuickFilterToolbarItem';
import { useFiltersHelpers } from 'src/components/FilterToolbar/useFiltersHelpers';
import { InventoryGlobalActionDropdown } from 'src/components/Listings/InventoryActionDropdown';
import {
  listingsMandatoryFiltersToShow,
  ViewModeSelector,
} from 'src/components/MainFilterBar';
import {
  PLACEHOLDER_EVENTS_QUICK_FILTER_ID,
  useIsPlaceholderEventsOnly,
} from 'src/contexts/CatalogDataContext/usePlaceholderEventsOnly';
import { Content } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import {
  QUICK_FILTER_SEARCH_PARAM_KEY,
  useFilterToolbarQuickFilters,
} from 'src/hooks/useQuickFilters';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { ContentId } from 'src/utils/constants/contentId';
import { INVENTORY_VIEW_MODE_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  ActionOutboxEntityType,
  Feature,
  InventoryViewMode,
  ListingQuery,
  UserSetting,
} from 'src/WebApiController';
import Swiper from 'swiper';

import { SidePanelButton } from '../../Buttons/SidePanelButton';
import { SwiperButton } from '../../Buttons/SwiperButton';
import { BackSection } from '../../common/BackSection';
import { useListingFilters } from '../../MainFilterBar';
import * as styles from '../../MainFilterBar/MainFilterBar.css';
import { CatchUpEventSearchBox } from '../InventoryEventPage/InventoryEventCatchUp/CatchUpEventSearchBox';
import { useCatchUpSearchContext } from '../InventoryEventPage/InventoryEventCatchUp/CatchUpSearchContext';
import { useCatchUpParams } from '../InventoryEventPage/InventoryEventPage.hooks';
import { useDefaultListingFilter } from './hooks/useDefaultListingFilter';
import { INVENTORY_VIEW_MODE_TO_ICON } from './ListingFilterBar.types';
import { ListingSearchBox } from './ListingSearchBox';

export function ListingFilterBar({
  isSearchOnly,
  isEventPage = false,
  returnUrl,
  viagVirtId,
  swiperRef,
}: {
  isSearchOnly?: boolean;
  isEventPage?: boolean;
  returnUrl?: string;
  viagVirtId?: string;
  swiperRef?: React.MutableRefObject<Swiper | undefined>;
}) {
  const isMobile = useMatchMedia('mobile');
  const { isCatchupMode } = useCatchUpParams();
  const hasCatchupModeEventSearch = useUserHasFeature(
    Feature.CatchupModeEventSearch
  );
  const { barPosition } = useCatchUpSearchContext();
  const { filters } = useListingFilters({
    isEventPage,
  });

  // "Placeholder Events" quick filter — a client-side filter (applied in
  // CatalogDataContextV2 via searchEvents) that shares the single-selection
  // quickFilterId URL param, so selecting any other quick filter deselects it.
  const hasPlaceholderEventFeature = useUserHasFeature(
    Feature.PlaceholderEventGroup
  );
  const placeholderEventsOnly = useIsPlaceholderEventsOnly();
  const [, setSearchParams] = useSearchParams();

  const {
    filterQuery: listingQuery,
    initialQuery,
    tempQuery,
    setFilterQuery,
    setInitialQuery,
    setTempQuery,
    resetTempQuery,
  } = useFilterQueryContext<ListingQuery>();

  const defaultQuickFilters = useDefaultListingFilter({
    initialQuery,
    listingQuery,
  });

  const placeholderQuickFilter = useMemo<QuickFilterToolbarItem[]>(
    () =>
      hasPlaceholderEventFeature && !isEventPage
        ? [
            {
              id: PLACEHOLDER_EVENTS_QUICK_FILTER_ID,
              name: (
                <span className={styles.placeholderFilterLabel}>
                  <Content id={ContentId.PlaceholderEvents} />
                </span>
              ),
              isCustomFilter: false,
              isSelected: placeholderEventsOnly,
              onClick: () => {
                // Reset the server query (like the other quick filters) and
                // toggle placeholder via the shared quickFilterId param —
                // selecting another quick filter overwrites it and turns this off.
                setFilterQuery(initialQuery);
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  if (placeholderEventsOnly) {
                    next.delete(QUICK_FILTER_SEARCH_PARAM_KEY);
                  } else {
                    next.set(
                      QUICK_FILTER_SEARCH_PARAM_KEY,
                      PLACEHOLDER_EVENTS_QUICK_FILTER_ID
                    );
                  }
                  return next;
                });
              },
            },
          ]
        : [],
    [
      hasPlaceholderEventFeature,
      isEventPage,
      placeholderEventsOnly,
      initialQuery,
      setFilterQuery,
      setSearchParams,
    ]
  );

  useEffect(() => {
    setTempQuery(listingQuery);
  }, [listingQuery, setTempQuery]);

  const { value: defaultViewModeUserSetting, setUserSetting } =
    useServerUserSetting<InventoryViewMode>({
      id: UserSetting.InventoryPageViewMode,
      currentLoginUserOnly: true,
    });

  const filterToolbarProps = useFilterToolbarQuickFilters({
    quickFiltersStateSetting: UserSetting.QuickFiltersStateInventory,
    customQuickFiltersSetting: UserSetting.QuickFiltersCustomInventory,
    defaultQuickFilters,
    currentQuery: listingQuery,
    initialQuery,
    favoriteQuickFilterSetting: UserSetting.FavoriteQuickFilterInventory,
    onSelect: ({ query }) => {
      if (query.viewMode != null) {
        // We need to update initialQuery, otherwise the quick filter "All"
        // may change the current view mode
        if (query.viewMode !== initialQuery.viewMode) {
          setInitialQuery({ ...initialQuery, viewMode: query.viewMode });
        }
      }

      setFilterQuery(query);

      // This needs to happen after to reduce re-rendering
      if (query.viewMode != null) {
        // Update user setting view mode too so it won't revert the filter query viewMode
        if (query.viewMode !== defaultViewModeUserSetting) {
          setUserSetting(query.viewMode);
        }
      }
    },
  });

  const { appliedFilters } = useFiltersHelpers({
    filters,
    activeQuery: listingQuery,
    mandatoryFiltersToShow: listingsMandatoryFiltersToShow,
    initialQuery,
  });

  const filterToolbar = listingQuery && (
    <FilterToolbar
      {...filterToolbarProps}
      additionalQuickFilters={placeholderQuickFilter}
      filterAppliedCounts={appliedFilters.length}
      filterAppliedIds={appliedFilters}
      onSubmitFilter={() => {
        setFilterQuery(tempQuery);
      }}
      filters={filters}
      resetTempQuery={resetTempQuery}
      tempQuery={tempQuery}
      setTempQuery={(partialQuery) => {
        setTempQuery({
          ...tempQuery,
          ...partialQuery,
        } as ListingQuery);
      }}
      mandatoryFiltersToShow={listingsMandatoryFiltersToShow}
    />
  );

  return isSearchOnly ? (
    <div className={styles.mainFilterBarDiv}>
      <div className={styles.dockLeftDiv}>
        <div className={styles.mainFilterBarLeftChild}>
          <SwiperButton dir="left" swiperRef={swiperRef} />
        </div>
      </div>
      <div className={styles.dockRightDiv}>
        <div className={styles.mainFilterBarRightChild}>
          <BulkEditStatusPopover entityType={ActionOutboxEntityType.Listing} />
          {!isMobile && (
            <ViewModeSelector
              query={listingQuery}
              entityType={ActionOutboxEntityType.Listing}
              initialQuery={initialQuery}
              setFilterQuery={setFilterQuery}
              setInitialQuery={setInitialQuery}
              viewModeSettingsId={UserSetting.InventoryPageViewMode}
              viewModeCidMap={INVENTORY_VIEW_MODE_TO_CID}
              viewModeIconMap={INVENTORY_VIEW_MODE_TO_ICON}
            />
          )}
          <MultiSelectionToggleGlobal />
          {!isEventPage && (
            <>
              <InventoryGlobalActionDropdown />

              <ListingSearchBox />
            </>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div className={styles.mainFilterBarDiv}>
      {isMobile ? (
        filterToolbar
      ) : (
        <>
          <div className={styles.dockLeftDiv}>
            <div className={styles.mainFilterBarLeftChild}>
              {isEventPage && (
                <BackSection
                  returnUrl={returnUrl ?? '/inventory'}
                  returnState={{
                    fromViagVirtId: viagVirtId,
                  }}
                />
              )}
              {listingQuery.viewMode === InventoryViewMode.MetricView && (
                <SidePanelButton position="relative" />
              )}
              {filterToolbar}
            </div>
          </div>
          <div className={styles.dockRightDiv}>
            <div className={styles.mainFilterBarRightChild}>
              {!isEventPage && (
                <>
                  <BulkEditStatusPopover
                    entityType={ActionOutboxEntityType.Listing}
                  />
                  <ViewModeSelector
                    query={listingQuery}
                    entityType={ActionOutboxEntityType.Listing}
                    initialQuery={initialQuery}
                    setFilterQuery={setFilterQuery}
                    setInitialQuery={setInitialQuery}
                    viewModeSettingsId={UserSetting.InventoryPageViewMode}
                    viewModeCidMap={INVENTORY_VIEW_MODE_TO_CID}
                    viewModeIconMap={INVENTORY_VIEW_MODE_TO_ICON}
                  />
                  <MultiSelectionToggleGlobal />
                  <InventoryGlobalActionDropdown />
                </>
              )}
              {isEventPage &&
                (isCatchupMode &&
                hasCatchupModeEventSearch &&
                barPosition !== 'side' ? (
                  <CatchUpEventSearchBox />
                ) : (
                  <ListingSearchBox isEventPage={isEventPage} />
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
